import { HeaderPageTitle } from 'src/context/header-title.context';
import { createRoute } from '@tanstack/react-router';
import { T, msg } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Spinner } from 'src/components/ui/spinner';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import {
  Route as pathlessAuthenticatedRoute,
  requireFeatureFlag,
  requirePermission,
} from '../../../route';
import { SettingsOrganizationLayoutPage } from '../SettingsOrganizationPage';
import { TaskTypeEditorBody } from './TaskTypeEditorBody';
import { TaskTypeDetailActions } from './TaskTypeDetailActions';
import { useTaskTypeDetailEditor } from './useTaskTypeDetailEditor';

/**
 * Task type detail — editor for one `core_task_types` row (TaskType
 * update to save, delete to archive; Task read to view).
 * Nested visually in Organization Settings (same chrome as the list) while
 * co-located as a file-router module under this URL path.
 */
function TaskTypeDetailPage() {
  const { taskTypeId } = Route.useParams();

  return (
    <SettingsOrganizationLayoutPage hideRail>
      <TaskTypeDetailContent key={taskTypeId} taskTypeId={taskTypeId} />
    </SettingsOrganizationLayoutPage>
  );
}

function TaskTypeDetailContent({ taskTypeId }: { taskTypeId: string }) {
  const {
    gt,
    canManage,
    canArchive,
    taskType,
    isLoading,
    isError,
    editor,
    canSave,
    isDirty,
    archiveOpen,
    setArchiveOpen,
    setDraft,
    stageOptions,
    unsavedGuard,
    returnToList,
    handleBack,
    handleSave,
    handleArchive,
    isSaving,
    isArchiving,
  } = useTaskTypeDetailEditor(taskTypeId);

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center py-16">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-sm text-muted-foreground">
          <T context="task type detail loading state">Loading task type…</T>
        </span>
      </div>
    );
  }

  if (isError || !taskType || !editor) {
    return (
      <div className="flex min-h-full flex-col gap-3 p-2">
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={returnToList}
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          <T context="task type detail back button">Back to task types</T>
        </Button>
        <p className="text-sm text-muted-foreground">
          <T context="task type detail not found message">
            This task type was not found. It may have been archived.
          </T>
        </p>
      </div>
    );
  }

  // fallow-ignore-next-line code-duplication
  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-10 bg-background flex flex-wrap items-start justify-between gap-3 px-2 py-2">
        <div className="min-w-0 flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-7 w-fit px-2 text-muted-foreground"
            onClick={handleBack}
          >
            <ArrowLeftIcon className="mr-1.5 h-4 w-4" />
            <T context="task type detail back button">Back to task types</T>
          </Button>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <HeaderPageTitle>{taskType.name}</HeaderPageTitle>
            <Badge variant="outline" className="font-mono font-normal">
              {taskType.key}
            </Badge>
            {isDirty ? (
              <Badge variant="secondary">
                <T context="badge when the task type editor has unsaved edits">
                  Unsaved
                </T>
              </Badge>
            ) : null}
          </div>
        </div>
        {canManage && (
          <TaskTypeDetailActions
            canArchive={canArchive}
            archiveDisabled={isDirty}
            saveDisabled={!canSave || isSaving}
            isSaving={isSaving}
            onArchive={() => setArchiveOpen(true)}
            onSave={() => void handleSave()}
          />
        )}
      </header>

      <TaskTypeEditorBody
        editor={editor}
        setDraft={setDraft}
        canManage={canManage}
        stageOptions={stageOptions}
        keyDisabled
        unsavedGuard={unsavedGuard}
      />

      <ConfirmDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        onConfirm={handleArchive}
        title={gt('Archive task type "{name}"?', {
          context: 'confirm dialog title for archiving a task type',
          name: taskType.name,
        })}
        message={gt(
          'Closed tasks keep their type for history. Archiving is blocked while open tasks still carry this type.',
          { context: 'confirm dialog body for archiving a task type' },
        )}
        confirmText={gt('Archive', {
          context: 'confirm button for archiving a task type',
        })}
        loading={isArchiving}
        closeOnConfirm={false}
        dangerous
      />
    </div>
  );
}

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/organization/task-types/$taskTypeId',
  staticData: {
    app: {
      access: {
        featureFlag: 'tasks-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Task }],
      },
    },
  },
  component: TaskTypeDetailPage,
  loader: () => ({
    crumb: msg('Task Type', { context: 'page breadcrumb' }),
    crumbTo: '/settings/organization/task-types',
    crumbAncestors: [
      {
        title: msg('Organization Settings', { context: 'page breadcrumb' }),
        path: '/settings/organization',
      },
    ],
  }),
  beforeLoad: async ({ context }) => {
    requireFeatureFlag('tasks-enabled');
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Task,
    });
  },
});
